/**
 * What a self-paced plan's right-hand half shows: the cursor illustration, then the order.
 *
 * ── There is no calendar to draw, and that is the point ─────────────────────────────────────
 * `self-paced-plan-requirements.md` R8: the calendar "MUST NOT place any dated row on any calendar
 * for an occurrence that has not been lived". So the only true answer to "when is this workout" is
 * "after the one before it", and position is what says that. `model/selfPacedReview.ts` decides
 * every word below; this file is a list.
 */

import { SelfPacedSequenceViz } from '~/shared/ui/planSystemViz';

import type { SelfPacedReview } from '../model/selfPacedReview';

import styles from './AssignPlanDialog.module.css';

interface SelfPacedListProps {
  readonly review: SelfPacedReview;
  readonly reducedMotion: boolean;
  readonly running: boolean;
}

export function SelfPacedList({ review, reducedMotion, running }: SelfPacedListProps) {
  return (
    <>
      <div className={styles.sequenceViz}>
        <SelfPacedSequenceViz reducedMotion={reducedMotion} running={running} />
      </div>

      <p className={styles.ambientNote}>{review.promise}</p>

      {review.emptyMessage !== null && <p className={styles.ambientNote}>{review.emptyMessage}</p>}

      {review.cycles.map((cycle) => (
        <section key={cycle.key} className={styles.cycle}>
          <h4 className={styles.cycleHeading}>{cycle.heading}</h4>
          <p className={styles.cycleCaption}>{cycle.phaseCaption}</p>
          <ol className={styles.cycleDays}>
            {cycle.days.map((day) => (
              <li key={day.key} className={styles.cycleDay}>
                <span className={styles.cyclePosition}>{day.position}</span>
                <span className={styles.cycleText}>
                  <span className={styles.cycleTitle}>{day.title}</span>
                  {day.sessions.map((session) => (
                    <span key={session.key} className={styles.cycleSession}>
                      {session.title}
                      {session.sessionLabel !== null && ` · ${session.sessionLabel}`}
                    </span>
                  ))}
                </span>
                {day.restNote !== null && <span className={styles.cycleRest}>{day.restNote}</span>}
              </li>
            ))}
          </ol>
        </section>
      ))}
    </>
  );
}
